import { useQuery, type Query } from "@tanstack/react-query";

import type { Basis, GridResponse, GridRow, Market } from "./api";
import { contributionCell, type AdsMode } from "./contribution";
import { pct1, rowKey, signedPct } from "./format";
import type { GridEdit } from "./GridPriceEdit";
import type { ViewKey } from "./order_views";
import { cleanPrice, skuOf } from "./price";
import { projectionBody, projectionQuery, projectionRefusal, PROJECTION_KEY, type ProjectBody, type Projection } from "./projection";
import { findRow, type VisibleRow } from "./rows";
import { cellBand, type CellBand, type SignalKey } from "./signal";
import { useDebounced } from "@/lib/useDebounced";

/**
 * C1 v1.65 (c1-live-row-preview, DEC-1662 A): the row in edit follows the typed price with C2's own figures and bands
 * (C2 v1.78). The portal picks which part of C2's answer feeds which cell; it never computes a figure or a band (H12, INV-12).
 */
export type RowPreview =
  | { kind: "none" }
  | { kind: "pending"; key: string; typed: string; held: Projection | null }
  | { kind: "answer"; key: string; typed: string; p: Projection }
  | { kind: "refused"; key: string; typed: string; text: string };

export const PREVIEW_COPY = {
  notSaved: (price: string) => `Showing ${price} · not saved`,
} as const;

/** The body for the row in edit: the drawer's body plus the view on screen. */
export function previewBody(row: Parameters<typeof projectionBody>[0], price: string, basis: Basis, asOf: string | null, view: ViewKey | null): ProjectBody {
  return { ...projectionBody(row, price, basis, asOf), ...(view === null ? {} : { view }) };
}

/** True when two projection keys differ only in the price: the held answer is the same row, basis, date and view. */
export function sameRowKey(a: readonly unknown[] | undefined, b: readonly unknown[]): boolean {
  if (!a || a.length !== b.length) return false;
  return a.every((part, i) => i === PROJECTION_KEY.length + 3 || part === b[i]);
}

/** The one row's preview: the typed price as it rests, C2's answer for it, and the last answer while a newer one is asked. */
export function useRowPreview(edit: GridEdit | null, grids: GridResponse[], basis: Basis, asOf: string | null, view: ViewKey | null): RowPreview {
  const rested = useDebounced(edit, 400);
  const found = edit ? findRow(grids, edit.key) : null;
  const typed = edit ? cleanPrice(edit.value) : null;
  const restedTyped = rested && edit && rested.key === edit.key ? cleanPrice(rested.value) : null;
  const body = found && restedTyped !== null ? previewBody(found.row, restedTyped, basis, asOf, view) : null;
  const q = projectionQuery(body ?? { xero_sku: "", new_price: "" });
  const query = useQuery({
    ...q, enabled: body !== null,
    placeholderData: (prev: Projection | undefined, prevQuery: Query<Projection> | undefined) => (sameRowKey(prevQuery?.queryKey, q.queryKey) ? prev : undefined),
  });
  return previewState(edit, typed, restedTyped, { data: query.data, error: query.error, isPlaceholderData: query.isPlaceholderData, isFetching: query.isFetching }, found !== null);
}

/** The state from the edit, the rested price and the query, pure so it is tested without a DOM. */
export function previewState(
  edit: GridEdit | null, typed: string | null, restedTyped: string | null,
  query: { data: Projection | undefined; error: unknown; isPlaceholderData: boolean; isFetching: boolean }, found: boolean,
): RowPreview {
  if (!edit || !found || typed === null) return { kind: "none" };
  if (restedTyped !== typed) return { kind: "pending", key: edit.key, typed, held: query.data ?? null };
  if (query.error) return { kind: "refused", key: edit.key, typed, text: projectionRefusal(query.error) };
  if (!query.data || query.isPlaceholderData) return { kind: "pending", key: edit.key, typed, held: query.data ?? null };
  return { kind: "answer", key: edit.key, typed, p: query.data };
}

/** What a visible row shows from the preview: C2's answer (dim while a newer one is asked), saved figures dimmed, or nothing. */
export type Live = { p: Projection | null; dim: boolean };
/** The row in edit follows, and so does each ship-country row of the store in edit (matched by SKU and store, in its market). */
export function liveFor(v: VisibleRow, preview: RowPreview, edited: { row: GridRow; market: Market } | null): Live | null {
  if (preview.kind === "none" || preview.kind === "refused") return null;
  const own = !v.child && rowKey(v.row) === preview.key;
  const child = v.child && edited !== null && v.market === edited.market && edited.row.grain === "store"
    && skuOf(v.row) === edited.row.xero_sku && v.row.channel_location === edited.row.channel_location;
  if (!own && !child) return null;
  if (preview.kind === "answer") return { p: preview.p, dim: false };
  return { p: preview.held, dim: true };
}

/** One cell's figure and band from C2's answer. `undefined` means the cell is not a preview cell (a ship country's Customer or
 * Competition). In a view, Contribution stays the single-order figure on the top-level band (DEC-1413 A); the other four read
 * `view_row` (E2); a ship country in a view reads its line of `view_row.figures` on neutral paint, as the saved view does. */
export type PreviewCell = { figure: string | null; entry: CellBand | null };
export function previewCell(p: Projection, key: SignalKey, v: VisibleRow, view: ViewKey | null, ads: AdsMode): PreviewCell | undefined {
  if (v.child) {
    if (key === "customer" || key === "competition") return undefined;
    const line = p.lines.find((l) => l.location === v.row.location);
    if (key === "contribution") {
      if (!line) return { figure: null, entry: null };
      const c = contributionCell(line.margins, ads);
      return { figure: c.kind === "value" ? c.text : null, entry: cellBand(line.bands, "contribution", ads) };
    }
    if (view === null) {
      if (!line) return { figure: null, entry: null };
      return { figure: line.margins[key === "gross" ? "gross_pct" : "fulfilment_pct"] == null ? null : pct1(line.margins[key === "gross" ? "gross_pct" : "fulfilment_pct"]), entry: cellBand(line.bands, key, ads) };
    }
    const vline = p.view_row?.figures?.lines.find((l) => l.location === v.row.location);
    const value = vline?.margins[key === "gross" ? "gross_pct" : "fulfilment_pct"];
    return { figure: value == null ? null : pct1(value), entry: null };
  }
  if (key === "contribution") {
    const c = contributionCell(p.margins, ads);
    return { figure: c.kind === "value" ? c.text : null, entry: cellBand(p.bands, "contribution", ads) };
  }
  const src = view === null ? { figures: p, bands: p.bands } : { figures: p.view_row?.not_viewed ? null : p.view_row?.figures ?? null, bands: p.view_row?.bands };
  if (!src.figures) return { figure: null, entry: null };
  const f = src.figures;
  const figure = key === "gross" ? (f.margins.gross_pct == null ? null : pct1(f.margins.gross_pct))
    : key === "fulfilment" ? (f.margins.fulfilment_pct == null ? null : pct1(f.margins.fulfilment_pct))
    : key === "customer" ? (f.metrics.vs_previous_price_pct.value == null ? null : signedPct(f.metrics.vs_previous_price_pct.value))
    : (f.metrics.vs_competitor_max_pct?.value == null ? null : signedPct(f.metrics.vs_competitor_max_pct.value));
  return { figure, entry: cellBand(src.bands, key, ads) };
}
